import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch

// Fáze 3: členská sekce. Nepřihlášený vidí login; přihlášený člen vidí dashboard s formulářem
// pro zadání/úpravu tréninku (skóre hráčům, dynamický počet her) a výpisem s možností upravit/smazat.
// Data ve vlastním sgbowling Supabase; přístup hlídá RLS (sg_is_member / sg_is_admin).

private const val GAMES_PER_SESSION = 3
private const val READ_ONLY_LOGIN = "nahled@sunnygang.example"

// Typy bowlingu. Kód (do DB) -> popisek. Default "classic" (klasika).
val BOWLING_TYPES = listOf(
    "classic" to "Klasika",
    "nine" to "Devítkový",
    "eight" to "Osmičkový",
    "narubak" to "Narubák",
)
fun bowlingTypeLabel(code: String?): String =
    BOWLING_TYPES.firstOrNull { it.first == code }?.second ?: "Klasika"

/** Cíl otevřeného editoru zápisu: buňka ("playerId#game") a její typ hry. */
data class EditorTarget(val key: String, val gameType: String)

@Composable
fun MembersScreen(
    token: String?,
    linkedReadOnlyPassword: String? = null,
    onLinkedAccessConsumed: () -> Unit = {},
    onToken: (String?) -> Unit,
) {
    // Uložený token už mohl vypršet (např. po delší nečinnosti) -> rovnou odhlásit, ať dashboard
    // nespadne na "JWT expired". Reaktivní pojistka na expiraci během používání je v MemberDashboard.
    LaunchedEffect(token) { if (token != null && isTokenExpired(token)) onToken(null) }
    if (token == null || isTokenExpired(token)) LoginForm(linkedReadOnlyPassword, onLinkedAccessConsumed, onToken) else MemberDashboard(token, onToken)
}

@Composable
private fun LoginForm(linkedReadOnlyPassword: String?, onLinkedAccessConsumed: () -> Unit, onToken: (String?) -> Unit) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var viewerPassword by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var busy by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(linkedReadOnlyPassword) {
        val access = linkedReadOnlyPassword?.takeIf { it.isNotBlank() } ?: return@LaunchedEffect
        viewerPassword = access
        busy = true; error = null
        try {
            val resp = Api.login(READ_ONLY_LOGIN, access)
            if (resp.accessToken != null) onToken(resp.accessToken)
            else error = resp.errorDescription ?: "Přihlášení selhalo"
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            error = "Přihlášení selhalo: ${e.apiMessage()}"
        } finally {
            busy = false
            onLinkedAccessConsumed()
        }
    }

    Div(attrs = { classes("card", "login-card") }) {
        H2 { Text("Přihlášení člena") }
        P(attrs = { classes("muted") }) { Text("Hráči a administrátoři se přihlašují svým účtem.") }
        Input(type = InputType.Email) {
            classes("field"); placeholder("e-mail"); value(email); onInput { email = it.value }
        }
        Input(type = InputType.Password) {
            classes("field"); placeholder("heslo"); value(password); onInput { password = it.value }
        }
        Button(attrs = {
            classes("primary-btn")
            if (busy) disabled()
            onClick {
                busy = true; error = null
                scope.launch {
                    try {
                        val resp = Api.login(email.trim(), password)
                        if (resp.accessToken != null) onToken(resp.accessToken)
                        else error = resp.errorDescription ?: "Přihlášení selhalo"
                    } catch (e: Throwable) {
                        error = "Přihlášení selhalo: ${e.apiMessage()}"
                    } finally {
                        busy = false
                    }
                }
            }
        }) { Text(if (busy) "Přihlašuji…" else "Přihlásit") }

        Div(attrs = { classes("login-divider") }) { Span { Text("nebo") } }
        H3 { Text("Přístup jen pro čtení") }
        P(attrs = { classes("muted") }) { Text("Sdílený přístup umožňuje prohlížet statistiky a výsledky bez možnosti cokoliv upravit.") }
        Input(type = InputType.Password) {
            classes("field"); placeholder("přístupové heslo"); value(viewerPassword); onInput { viewerPassword = it.value }
        }
        Button(attrs = {
            classes("small-btn", "viewer-login-btn")
            if (busy || viewerPassword.isBlank()) disabled()
            onClick {
                busy = true; error = null
                scope.launch {
                    try {
                        val resp = Api.login(READ_ONLY_LOGIN, viewerPassword)
                        if (resp.accessToken != null) onToken(resp.accessToken)
                        else error = resp.errorDescription ?: "Přihlášení selhalo"
                    } catch (e: Throwable) {
                        error = "Přihlášení selhalo: ${e.apiMessage()}"
                    } finally {
                        busy = false
                    }
                }
            }
        }) { Text(if (busy) "Přihlašuji…" else "Vstoupit jen pro čtení") }
        error?.let { Div(attrs = { classes("form-error") }) { Text(it) } }
    }
}

@Composable
private fun MemberDashboard(token: String, onToken: (String?) -> Unit) {
    var member by remember { mutableStateOf<SgMemberDto?>(null) }
    var profiles by remember { mutableStateOf<List<SgProfileDto>>(emptyList()) }
    // tréninky
    var trainings by remember { mutableStateOf<List<SgTrainingDto>>(emptyList()) }
    var scoresByTraining by remember { mutableStateOf<Map<String, List<SgScoreDto>>>(emptyMap()) }
    var sheetsByTraining by remember { mutableStateOf<Map<String, List<SgScoresheetDto>>>(emptyMap()) }
    var trainingEditing by remember { mutableStateOf<Triple<SgTrainingDto, List<SgScoreDto>, List<SgScoresheetDto>>?>(null) }
    // turnaje
    var tournaments by remember { mutableStateOf<List<SgTournamentDto>>(emptyList()) }
    var scoresByTournament by remember { mutableStateOf<Map<String, List<SgScoreDto>>>(emptyMap()) }
    var sheetsByTournament by remember { mutableStateOf<Map<String, List<SgScoresheetDto>>>(emptyMap()) }
    var placementsByTournament by remember { mutableStateOf<Map<String, List<SgPlacementDto>>>(emptyMap()) }
    var tournamentEditing by remember { mutableStateOf<TournamentEdit?>(null) }

    var tab by remember { mutableStateOf("stats") }
    // Přidání nového tréninku/turnaje je defaultně schované pod tlačítkem (formulář zabírá místo).
    var showTrainingForm by remember { mutableStateOf(false) }
    var showTournamentForm by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var reloadKey by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(reloadKey) {
        try {
            member = Api.fetchMyMember(token)
            profiles = Api.fetchSgProfiles().filter { it.isActive }
            trainings = Api.fetchTrainings(token)
            val tIds = trainings.map { it.id }
            scoresByTraining = Api.fetchTrainingScores(token, tIds).groupBy { it.trainingId ?: "" }
            sheetsByTraining = Api.fetchTrainingSheets(token, tIds).groupBy { it.trainingId ?: "" }
            tournaments = Api.fetchTournaments(token)
            val uIds = tournaments.map { it.id }
            scoresByTournament = Api.fetchTournamentScores(token, uIds).groupBy { it.tournamentId ?: "" }
            sheetsByTournament = Api.fetchTournamentSheets(token, uIds).groupBy { it.tournamentId ?: "" }
            placementsByTournament = Api.fetchTournamentPlacements(token, uIds).groupBy { it.tournamentId ?: "" }
        } catch (e: Throwable) {
            if (e.isUnauthorized()) { onToken(null); return@LaunchedEffect } // token vypršel -> odhlásit
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    val email = remember(token) { decodeJwtClaims(token)?.email }
    val readOnly = member?.role == "viewer"

    Div(attrs = { classes("card", "member-bar") }) {
        Div {
            Span(attrs = { classes("member-email") }) { Text(if (readOnly) "Sdílený přístup" else email ?: "člen") }
            member?.let { m ->
                val roleLabel = when (m.role) { "viewer" -> "jen čtení"; "admin" -> "admin"; else -> "hráč" }
                Span(attrs = { classes(*(listOfNotNull("badge", "badge-admin".takeIf { m.role == "admin" }).toTypedArray())) }) { Text(roleLabel) }
            }
        }
        Button(attrs = { classes("back-button"); onClick { onToken(null) } }) { Text("Odhlásit") }
    }

    Div(attrs = { classes("tab-bar") }) {
        Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { tab == "stats" }).toTypedArray())); onClick { tab = "stats" } }) { Text("Statistiky týmu") }
        Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { tab == "playerstats" }).toTypedArray())); onClick { tab = "playerstats" } }) { Text("Statistiky hráčů") }
        Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { tab == "training" }).toTypedArray())); onClick { tab = "training" } }) { Text("Tréninky") }
        Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { tab == "tournament" }).toTypedArray())); onClick { tab = "tournament" } }) { Text("Turnaje") }
        Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { tab == "league" }).toTypedArray())); onClick { tab = "league" } }) { Text("Ligové zápasy") }
        if (member?.role == "admin") {
            Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { tab == "content" }).toTypedArray())); onClick { tab = "content" } }) { Text("Obsah webu") }
            Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { tab == "members" }).toTypedArray())); onClick { tab = "members" } }) { Text("Členové") }
            Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { tab == "children" }).toTypedArray())); onClick { tab = "children" } }) { Text("Děti") }
        }
    }

    when {
        loading -> Div(attrs = { classes("card") }) { P { Text("Načítám…") } }
        error != null -> Div(attrs = { classes("card") }) { P { Text("Chyba: $error") } }
        tab == "training" -> {
            if (!readOnly && (showTrainingForm || trainingEditing != null)) {
                key(trainingEditing?.first?.id) {
                    TrainingForm(token, member, profiles, trainingEditing,
                        onSaved = { trainingEditing = null; showTrainingForm = false; reloadKey++ },
                        onCancel = { trainingEditing = null; showTrainingForm = false })
                }
            } else if (!readOnly) {
                Div(attrs = { classes("card") }) {
                    Button(attrs = { classes("primary-btn"); onClick { showTrainingForm = true } }) { Text("+ Nový trénink") }
                }
            }
            TrainingsList(
                trainings = trainings,
                scoresByTraining = scoresByTraining,
                profiles = profiles,
                readOnly = readOnly,
                onEdit = { t -> trainingEditing = Triple(t, scoresByTraining[t.id] ?: emptyList(), sheetsByTraining[t.id] ?: emptyList()) },
                onDelete = { t ->
                    if (window.confirm("Smazat trénink ${t.date}?")) {
                        scope.launch {
                            try {
                                Api.deleteTraining(token, t.id)
                                if (trainingEditing?.first?.id == t.id) trainingEditing = null
                                reloadKey++
                            } catch (e: Throwable) { error = "Smazání selhalo: ${e.apiMessage()}" }
                        }
                    }
                },
            )
        }
        tab == "tournament" -> {
            if (!readOnly && (showTournamentForm || tournamentEditing != null)) {
                key(tournamentEditing?.tournament?.id) {
                    TournamentForm(token, member, profiles, tournamentEditing,
                        onSaved = { tournamentEditing = null; showTournamentForm = false; reloadKey++ },
                        onCancel = { tournamentEditing = null; showTournamentForm = false })
                }
            } else if (!readOnly) {
                Div(attrs = { classes("card") }) {
                    Button(attrs = { classes("primary-btn"); onClick { showTournamentForm = true } }) { Text("+ Nový turnaj") }
                }
            }
            TournamentsList(
                tournaments = tournaments,
                scoresByTournament = scoresByTournament,
                placementsByTournament = placementsByTournament,
                profiles = profiles,
                readOnly = readOnly,
                onEdit = { t -> tournamentEditing = TournamentEdit(t, scoresByTournament[t.id] ?: emptyList(), sheetsByTournament[t.id] ?: emptyList(), placementsByTournament[t.id] ?: emptyList()) },
                onDelete = { t ->
                    if (window.confirm("Smazat turnaj ${t.name}?")) {
                        scope.launch {
                            try {
                                Api.deleteTournament(token, t.id)
                                if (tournamentEditing?.tournament?.id == t.id) tournamentEditing = null
                                reloadKey++
                            } catch (e: Throwable) { error = "Smazání selhalo: ${e.apiMessage()}" }
                        }
                    }
                },
            )
        }
        tab == "league" -> {
            LeagueSection(token, member, profiles, readOnly)
        }
        tab == "stats" -> {
            StatsSection(token, profiles)
        }
        tab == "content" && member?.role == "admin" -> {
            ContentAdminSection(token)
        }
        tab == "members" && member?.role == "admin" -> {
            MembersAdminSection(token)
        }
        tab == "children" && member?.role == "admin" -> {
            ChildrenAdminSection(token, member)
        }
        else -> {
            PlayerStatsSection(token, member, profiles, readOnly)
        }
    }
}

@Composable
private fun TrainingForm(
    token: String,
    member: SgMemberDto?,
    profiles: List<SgProfileDto>,
    editing: Triple<SgTrainingDto, List<SgScoreDto>, List<SgScoresheetDto>>?,
    onSaved: () -> Unit,
    onCancel: () -> Unit,
) {
    val isEdit = editing != null
    var date by remember { mutableStateOf(editing?.first?.date ?: kotlin.js.Date().toISOString().substring(0, 10)) }
    var note by remember { mutableStateOf(editing?.first?.notes ?: "") }
    var games by remember { mutableStateOf(editing?.second?.maxOfOrNull { it.gameNo } ?: GAMES_PER_SESSION) }
    val scores = remember {
        mutableStateMapOf<String, String>().apply {
            editing?.second?.forEach { put("${it.blPlayerId}#${it.gameNo}", it.score.toString()) }
        }
    }
    // typ bowlingu per sloupec (hra); default "classic". Předvyplní se z existujících skóre při úpravě.
    val gameTypes = remember {
        mutableStateMapOf<Int, String>().apply {
            editing?.second?.forEach { put(it.gameNo, it.gameType) }
        }
    }
    // kompletní zápisy per buňka ("playerId#game" -> rámce). Když existuje, skóre je z něj dopočítané.
    val sheets = remember {
        mutableStateMapOf<String, List<FrameDto>>().apply {
            editing?.third?.forEach { put("${it.blPlayerId}#${it.gameNo}", it.frames) }
        }
    }
    var editorTarget by remember { mutableStateOf<EditorTarget?>(null) }
    // Selecty s typem hry jsou skryté (default klasika); settings tlačítko je zobrazí/schová.
    var showTypes by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Div(attrs = { classes("card") }) {
        H3 { Text(if (isEdit) "Úprava tréninku" else "Nový trénink") }
        Div(attrs = { classes("training-meta") }) {
            Label { Text("Datum"); Input(type = InputType.Date) { classes("field"); value(date); onInput { date = it.value } } }
            Label { Text("Poznámka"); Input(type = InputType.Text) { classes("field"); placeholder("nepovinné"); value(note); onInput { note = it.value } } }
            Div(attrs = { classes("game-controls") }) {
                Button(attrs = { classes("small-btn"); onClick { games++ } }) { Text("+ přidat hru") }
                Button(attrs = { classes("small-btn"); if (games <= 1) disabled(); onClick { if (games > 1) games-- } }) { Text("− odebrat hru") }
                Button(attrs = {
                    classes(*(listOfNotNull("small-btn", "icon-btn", "small-btn-on".takeIf { showTypes }).toTypedArray()))
                    attr("title", "Typy her")
                    attr("aria-label", "Typy her")
                    onClick { showTypes = !showTypes }
                }) { Text("⚙") }
            }
        }

        PhotoImport(profiles, sheets, scores, typeForGame = { gameTypes[it] ?: "classic" },
            onGames = { games = maxOf(games, it) }, training = true)

        ScoreEntryTable(
            profiles = profiles,
            games = games,
            showTypes = showTypes,
            gameTypes = gameTypes,
            defaultType = "classic",
            scores = scores,
            sheets = sheets,
            onOpenEditor = { key, type -> editorTarget = EditorTarget(key, type) },
        )

        Div(attrs = { classes("form-actions") }) {
            Button(attrs = {
                classes("primary-btn")
                if (busy) disabled()
                onClick {
                    val createdBy = member?.userId ?: decodeJwtClaims(token)?.sub
                    if (createdBy == null) { message = "Chybí účet člena."; return@onClick }
                    val entries = profiles.flatMap { p ->
                        (1..games).mapNotNull { g ->
                            scores["${p.blPlayerId}#$g"]?.trim()?.toIntOrNull()?.let {
                                ScoreEntry(p.blPlayerId, g, it, gameTypes[g] ?: "classic")
                            }
                        }
                    }
                    if (entries.isEmpty()) { message = "Zadej aspoň jedno skóre."; return@onClick }
                    val sheetEntries = sheets.mapNotNull { (key, frames) ->
                        val parts = key.split("#")
                        val g = parts.getOrNull(1)?.toIntOrNull() ?: return@mapNotNull null
                        if (g > games) return@mapNotNull null // sloupec byl odebrán
                        val type = gameTypes[g] ?: "classic"
                        SheetEntry(parts[0], g, type, frames, Scoring.total(type, frames.map { it.t }))
                    }
                    busy = true; message = null
                    scope.launch {
                        try {
                            if (isEdit) {
                                val id = editing!!.first.id
                                Api.updateTraining(token, id, date, note)
                                Api.deleteTrainingScores(token, id)   // nahradíme kompletně
                                Api.addTrainingScores(token, id, entries)
                                Api.deleteTrainingSheets(token, id)
                                Api.addTrainingSheets(token, id, createdBy, sheetEntries)
                            } else {
                                val trId = Api.createTraining(token, createdBy, date, null, note)
                                Api.addTrainingScores(token, trId, entries)
                                Api.addTrainingSheets(token, trId, createdBy, sheetEntries)
                                scores.clear(); sheets.clear(); note = ""
                            }
                            message = null
                            onSaved()
                        } catch (e: Throwable) {
                            message = "Uložení selhalo: ${e.apiMessage()}"
                        } finally {
                            busy = false
                        }
                    }
                }
            }) { Text(if (busy) "Ukládám…" else if (isEdit) "Uložit změny" else "Uložit trénink") }
            Button(attrs = { classes("small-btn"); onClick { onCancel() } }) { Text("Zrušit") }
        }
        message?.let { Div(attrs = { classes("form-note") }) { Text(it) } }

        editorTarget?.let { target ->
            ScoresheetEditor(
                gameType = target.gameType,
                initialFrames = sheets[target.key] ?: emptyList(),
                onSave = { frames, total ->
                    sheets[target.key] = frames
                    scores[target.key] = total.toString()
                    editorTarget = null
                },
                onClose = { editorTarget = null },
                onDelete = { sheets.remove(target.key); editorTarget = null },
            )
        }
    }
}

@Composable
private fun TrainingsList(
    trainings: List<SgTrainingDto>,
    scoresByTraining: Map<String, List<SgScoreDto>>,
    profiles: List<SgProfileDto>,
    readOnly: Boolean,
    onEdit: (SgTrainingDto) -> Unit,
    onDelete: (SgTrainingDto) -> Unit,
) {
    val nameById = remember(profiles) { profiles.associate { it.blPlayerId to it.displayName } }
    Div(attrs = { classes("card") }) {
        H3 { Text("Odehrané tréninky") }
        if (trainings.isEmpty()) {
            P(attrs = { classes("muted") }) { Text("Zatím žádné tréninky.") }
            return@Div
        }
        Div(attrs = { classes("training-list") }) {
            trainings.forEach { t ->
                val scores = scoresByTraining[t.id] ?: emptyList()
                Div(attrs = { classes("training-item") }) {
                    Div(attrs = { classes("training-item-top") }) {
                        Div(attrs = { classes("training-head") }) {
                            Span(attrs = { classes("training-date") }) { Text(t.date) }
                            t.notes?.let { Span(attrs = { classes("muted") }) { Text(" · $it") } }
                            Span(attrs = { classes("muted") }) { Text(" · ${scores.size} skóre") }
                        }
                        if (!readOnly) {
                            Div(attrs = { classes("training-actions") }) {
                                Button(attrs = { classes("small-btn"); onClick { onEdit(t) } }) { Text("Upravit") }
                                Button(attrs = { classes("small-btn", "danger-btn"); onClick { onDelete(t) } }) { Text("Smazat") }
                            }
                        }
                    }
                    val byPlayer = scores.groupBy { it.blPlayerId }
                    Div(attrs = { classes("training-players") }) {
                        byPlayer.entries.sortedByDescending { it.value.map { s -> s.score }.average() }.forEach { (pid, sc) ->
                            val avg = (sc.map { it.score }.average() * 10).toInt() / 10.0
                            Span(attrs = { classes("training-player") }) { Text("${nameById[pid] ?: pid}: Ø $avg (${sc.size})") }
                        }
                    }
                }
            }
        }
    }
}
